import { useEffect, useMemo, useState } from "react";
import apiClient from "../../../../shared/api/client";
import { AUDIT_STAGE_DEPT, AUDIT_STATUSES, C } from "../../../../constants";
import { mapAudit, mapTaskTemplateCategory } from "../mappers";
import { uid } from "../../../../utils";
import RichCommentInput from "../../../../shared/comments/RichCommentInput";
import { CommentContent } from "../../../../shared/comments/commentContent";
import {
  Button,
  FieldGroup,
  FieldLabel,
  Input,
  Select,
  Timeline,
  DialogPrimitives,
  SheetPrimitives,
} from "../../../../shared/ui";
import { cn } from "../../../../lib/cn";

function createDraftState(audit) {
  return {
    name: audit?.name || "",
    driveLink: audit?.driveLink || "",
    estimator: audit?.estimator || "",
    contract: audit?.contract || "",
    transcripts: audit?.transcripts || "",
    lastUpdate: audit?.lastUpdate || "",
  };
}

function statusColor(status) {
  return ({
    Questionnaire:'#f59e0b',
    Interviews:'#60a5fa',
    Done:'#10b981',
    'General Task':'#64748b'
  }[status] || '#8fa4c8');
}

function AuditDrawerPanel({
  audit,
  currentUser,
  users,
  teams,
  taskTemplateCategories,
  onClose,
  onDelete,
  onStatusChange,
  onDeleteComment,
  onSaveAuditField,
  onGenerateTasks,
  onCreateAuditTask,
  onUpdateAuditTask,
  onDeleteAuditTask,
  onCreateAuditTaskComment,
  addNotification,
  showToast,
  auditStages,
  stageColorMap,
}) {
  // Resolve stage color: prefer dynamic map, fallback to static helper
  function resolveStageColor(s) { return stageColorMap?.[s] || statusColor(s); }
  const auditId = audit?.id;
  const [tab, setTab] = useState("overview");
  const [draft, setDraft] = useState(() => createDraftState(audit));
  const [commentDraft, setCommentDraft] = useState("");
  const [taskDraft, setTaskDraft] = useState({ title: "", description: "" });
  const [taskCommentDrafts, setTaskCommentDrafts] = useState({});

  useEffect(() => {
    setDraft(createDraftState(audit));
    setCommentDraft("");
    setTaskDraft({ title: "", description: "" });
    setTaskCommentDrafts({});
    setTab("overview");
    // Keep the local form stable while typing; only reset when a different audit is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId]);

  if (!audit) return null;

  async function saveField(field, value) {
    try {
      await onSaveAuditField(audit.id, field, value);
    } catch (error) {
      showToast(error.message || "Save failed", "error");
    }
  }


  async function handleCreateComment({ html, mentions } = {}) {
    const content = html || commentDraft;
    if (!content) return;
    try {
      await onSaveAuditField(audit.id, "__createComment", { content, mentions: mentions || [] });
      setCommentDraft("");
    } catch (error) {
      showToast(error.message || "Failed to add comment", "error");
    }
  }

  async function handleTaskCommentCreate(taskId, { html, mentions } = {}) {
    const content = html || taskCommentDrafts[taskId] || "";
    if (!content) return;
    try {
      await onCreateAuditTaskComment(audit.id, taskId, { content, mentions: mentions || [] });
      setTaskCommentDrafts((prev) => ({ ...prev, [taskId]: "" }));
    } catch (error) {
      showToast(error.message || "Failed to add task comment", "error");
    }
  }

  return (
    <SheetPrimitives.Sheet open={!!audit} onOpenChange={(open) => !open && onClose()}>
      <SheetPrimitives.SheetContent side="right">
        <SheetPrimitives.SheetHeader>
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            <input
              className={cn(
                "w-full bg-transparent border-0 outline-none",
                "text-[18px] font-extrabold tracking-[-0.01em] text-rc-text-primary",
                "focus:ring-2 focus:ring-rc-accent-strong/30 rounded",
              )}
              value={draft.name}
              onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
              onBlur={() => saveField("name", draft.name)}
            />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap" style={{ background: `${resolveStageColor(audit.status)}22`, color: resolveStageColor(audit.status), border: `1px solid ${resolveStageColor(audit.status)}44` }}>
                {audit.status}
              </span>
              {audit.type && <span className="aud-type-badge">{audit.type}</span>}
              <span className="text-[11.5px] text-rc-text-secondary">{audit.contact || "No contact"} · {audit.language || "No language set"}</span>
            </div>
          </div>
        </SheetPrimitives.SheetHeader>

        {/* Tabs */}
        <div className="shrink-0 flex items-center gap-1 px-6 py-2 border-b border-rc-border bg-rc-surface">
          {["overview", "tasks", "history", "comments"].map((section) => {
            const count = section==="tasks" ? (audit.auditTasks?.length||0) : section==="comments" ? (audit.comments?.length||0) : section==="history" ? (audit.stageHistory?.length||0) : null;
            return (
              <button
                key={section}
                onClick={() => setTab(section)}
                className={cn(
                  "px-3 py-1.5 rounded-md text-[12.5px] font-semibold cursor-pointer transition-colors",
                  "flex items-center gap-1.5",
                  tab === section
                    ? "bg-[rgba(59,127,250,0.14)] text-rc-accent-strong"
                    : "text-rc-text-secondary hover:text-rc-text-primary",
                )}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
                {count !== null && count > 0 && (
                  <span className={cn(
                    "font-mono text-[9.5px] px-1.5 rounded-[10px]",
                    tab === section ? "bg-[rgba(59,127,250,0.25)] text-rc-accent-strong" : "bg-rc-border text-rc-text-secondary",
                  )}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <SheetPrimitives.SheetBody>
          {tab === "overview" && (
            <>
              <div className="mt-5">
                <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Overview</div>
                <div className="grid grid-cols-3 gap-2.5">
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="text-[9px] font-semibold text-[#4a6a96] uppercase tracking-[0.03em] mb-0.5">Status</div><div className="text-[13px] font-semibold text-rc-text-primary"style={{ color: resolveStageColor(audit.status) }}>{audit.status}</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="text-[9px] font-semibold text-[#4a6a96] uppercase tracking-[0.03em] mb-0.5">Last Update</div><div className="text-[13px] font-semibold text-rc-text-primary">{audit.lastUpdate || "—"}</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="text-[9px] font-semibold text-[#4a6a96] uppercase tracking-[0.03em] mb-0.5">Department</div><div className="text-[13px] font-semibold text-rc-text-primary">{AUDIT_STAGE_DEPT[audit.status]?.dept || "—"}</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="text-[9px] font-semibold text-[#4a6a96] uppercase tracking-[0.03em] mb-0.5">Contact</div><div className="text-[13px] font-semibold text-rc-text-primary">{audit.contact || "—"}</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="text-[9px] font-semibold text-[#4a6a96] uppercase tracking-[0.03em] mb-0.5">Language</div><div className="text-[13px] font-semibold text-rc-text-primary">{audit.language || "—"}</div></div>
                  <div className="bg-[#0e1c38] border border-[#14213d] rounded-lg px-3 py-2.5"><div className="text-[9px] font-semibold text-[#4a6a96] uppercase tracking-[0.03em] mb-0.5">Participants</div><div className="text-[13px] font-semibold text-rc-text-primary">{audit.subitems?.length || 0}</div></div>
                </div>
              </div>

              <div className="mt-5">
                <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Audit Details</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] font-bold text-[#4a6a96] uppercase tracking-[0.04em]">Task Category</span>
                    <select
                      className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent"
                      value={audit.taskTemplateCategoryId || ""}
                      onChange={(e)=>saveField("taskTemplateCategoryId", e.target.value || null)}
                    >
                      <option value="">Select category…</option>
                      {taskTemplateCategories.map((category)=>(
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] font-bold text-[#4a6a96] uppercase tracking-[0.04em]">Drive Link</span>
                    <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={draft.driveLink} onChange={(e)=>setDraft((prev)=>({...prev,driveLink:e.target.value}))} onBlur={()=>saveField("driveLink", draft.driveLink)} placeholder="Paste URL…" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] font-bold text-[#4a6a96] uppercase tracking-[0.04em]">Last Update</span>
                    <input type="date" className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={draft.lastUpdate} onChange={(e)=>setDraft((prev)=>({...prev,lastUpdate:e.target.value}))} onBlur={()=>saveField("lastUpdate", draft.lastUpdate)} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] font-bold text-[#4a6a96] uppercase tracking-[0.04em]">Estimator</span>
                    <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={draft.estimator} onChange={(e)=>setDraft((prev)=>({...prev,estimator:e.target.value}))} onBlur={()=>saveField("estimator", draft.estimator)} placeholder="—" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] font-bold text-[#4a6a96] uppercase tracking-[0.04em]">Contract</span>
                    <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={draft.contract} onChange={(e)=>setDraft((prev)=>({...prev,contract:e.target.value}))} onBlur={()=>saveField("contract", draft.contract)} placeholder="—" />
                  </div>
                  <div className="flex flex-col gap-1 col-span-full">
                    <span className="text-[9px] font-bold text-[#4a6a96] uppercase tracking-[0.04em]">Transcripts</span>
                    <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={draft.transcripts} onChange={(e)=>setDraft((prev)=>({...prev,transcripts:e.target.value}))} onBlur={()=>saveField("transcripts", draft.transcripts)} placeholder="—" />
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Change Stage</div>
                <div className="flex gap-1 flex-wrap mb-1.5">
                  {(auditStages||[]).map((status)=>(
                    <div
                      key={status}
                      className={`aud-stage-pill${audit.status===status?' current':''}`}
                      style={{ background: audit.status===status ? `${resolveStageColor(status)}1a` : "rgba(14,27,50,.5)", color: resolveStageColor(status), borderColor: audit.status===status ? resolveStageColor(status) : "transparent" }}
                      onClick={()=>{ if(audit.status!==status) onStatusChange(audit.id, status); }}
                    >
                      {audit.status===status && <span style={{ fontSize: 8 }}>●</span>}
                      {status}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Task Templates</div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    className="bg-[rgba(59,127,250,0.12)] border border-[rgba(59,127,250,0.24)] text-rc-accent-strong rounded-md px-3.5 py-1.5 text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap hover:bg-[rgba(59,127,250,0.2)] disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={()=>onGenerateTasks(audit.id, audit.taskTemplateCategoryId, false)}
                    disabled={!audit.taskTemplateCategoryId}
                  >
                    Generate Tasks
                  </button>
                  <button
                    className="bg-transparent border border-rc-border text-rc-text-secondary rounded-md px-4 py-1.5 text-xs font-semibold cursor-pointer transition-colors font-sans hover:bg-rc-surface-hover disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={()=>onGenerateTasks(audit.id, audit.taskTemplateCategoryId, true)}
                    disabled={!audit.taskTemplateCategoryId}
                  >
                    Regenerate
                  </button>
                </div>
              </div>

              <div className="mt-5">
                <button className="bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] text-[#f87171] rounded-md px-4 py-1.5 text-xs font-semibold cursor-pointer transition-colors hover:bg-[rgba(239,68,68,0.18)]" onClick={() => onDelete(audit.id)}>Delete Audit</button>
              </div>
            </>
          )}

          {tab === "tasks" && (
            <div className="mt-5">
              <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Tasks ({audit.auditTasks?.length || 0})</div>
              {(audit.auditTasks || []).length === 0 && <div className="text-[12px] text-[#4a6a96] py-5 text-center">No tasks yet.</div>}
              {(audit.auditTasks || []).map((task) => {
                const isExpanded = taskDraft._expandedId === task.id;
                const comments = task.comments || [];
                return (
                  <div key={task.id} className={`kb-task-card${isExpanded ? " expanded" : ""}`}>
                    <div className="kb-task-row">
                      <div
                        className={`kb-task-check${task.status === "Done" ? " done" : ""}`}
                        onClick={() => onUpdateAuditTask(audit.id, task.id, { status: task.status === "Done" ? "To Do" : "Done" })}
                      >
                        {task.status === "Done" && <span style={{ color: "#fff", fontSize: 10, lineHeight: 1 }}>✓</span>}
                      </div>
                      <button
                        className={`kb-task-label-btn${task.status === "Done" ? " done" : ""}`}
                        onClick={() => setTaskDraft((prev) => ({ ...prev, _expandedId: prev._expandedId === task.id ? null : task.id }))}
                      >
                        {task.title}
                      </button>
                      <select className="kb-assignee-sel" value={task.assigneeId || ""} onChange={(e) => onUpdateAuditTask(audit.id, task.id, { assigneeId: e.target.value || null })}>
                        <option value="">Assign…</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                      <input type="date" className="kb-due-input" value={task.dueDate || ""} onChange={(e) => onUpdateAuditTask(audit.id, task.id, { dueDate: e.target.value || null })} />
                      {comments.length > 0 && <span className="kb-task-meta">{comments.length}💬</span>}
                      <button className="kb-task-remove" onClick={() => onDeleteAuditTask(audit.id, task.id)}>×</button>
                    </div>
                    {isExpanded && (
                      <div className="kb-task-detail">
                        {comments.length === 0 && <div className="kb-task-empty">No updates yet.</div>}
                        {comments.map((comment) => (
                          <div key={comment.id} className="aud-comment-row kb-comment-row">
                            <div className="aud-comment-header">
                              <span className="aud-comment-author">{comment.user?.name || "—"}</span>
                              <span className="aud-comment-ts">{comment.createdAt?.slice(0, 10)}</span>
                            </div>
                            <CommentContent value={comment.content} className="aud-comment-text rc-comment-body" />
                          </div>
                        ))}
                        <div className="kb-update-box">
                          <RichCommentInput
                            collapsible
                            rows={3}
                            placeholder="Write an update…"
                            value={taskCommentDrafts[task.id] || ""}
                            onChange={(val) => setTaskCommentDrafts((prev) => ({ ...prev, [task.id]: val }))}
                            onSubmit={(payload) => handleTaskCommentCreate(task.id, payload)}
                            users={users}
                            teams={teams}
                            mentionScopeLabel="task"
                            submitLabel="Post"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="kb-new-task-row">
                <input
                  placeholder="Add task…"
                  value={taskDraft.title || ""}
                  onChange={(e) => setTaskDraft((prev) => ({ ...prev, title: e.target.value }))}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter" && e.target.value.trim()) {
                      await onCreateAuditTask(audit.id, { title: e.target.value.trim(), description: "", categoryId: audit.taskTemplateCategoryId || null });
                      setTaskDraft((prev) => ({ ...prev, title: "" }));
                    }
                  }}
                />
                <button onClick={async () => {
                  if (!taskDraft.title?.trim()) return;
                  await onCreateAuditTask(audit.id, { title: taskDraft.title.trim(), description: "", categoryId: audit.taskTemplateCategoryId || null });
                  setTaskDraft((prev) => ({ ...prev, title: "" }));
                }}>Add</button>
              </div>
            </div>
          )}

          {tab === "history" && (
            <div className="mt-5">
              <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Stage History</div>
              {(audit.stageHistory || []).length === 0
                ? <div className="text-[12px] text-[#4a6a96] py-5 text-center">No history yet.</div>
                : (
                  <Timeline items={(audit.stageHistory || []).map((history, index, arr) => ({
                    title: history.stage,
                    meta: history.enteredAt ? new Date(history.enteredAt).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "—",
                    body: history.by ? `By ${history.by}` : undefined,
                    tone: index === arr.length - 1 ? "active" : "default",
                  }))} />
                )
              }
            </div>
          )}

          {tab === "comments" && (
            <div className="mt-5">
              <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Comments ({(audit.comments||[]).length})</div>
              <div className="aud-comment-list">
                {(audit.comments||[]).length===0 && <p className="text-[12px] text-[#4a6a96] py-5 text-center">No comments yet.</p>}
                {(audit.comments||[]).map((comment)=>(
                  <div key={comment.id} className="aud-comment-row">
                    <div className="aud-comment-header">
                      <span className="aud-comment-author">{comment.user?.name || "—"}</span>
                      <span className="aud-comment-ts">{comment.createdAt?.slice(0,10)}</span>
                      {comment.userId===currentUser?.id && <button className="bg-transparent border-0 text-[#2a3f60] cursor-pointer text-[11px] px-1.5 py-0.5 rounded transition-colors hover:bg-[rgba(239,68,68,0.12)] hover:text-[#f87171]" onClick={()=>onDeleteComment(audit.id, comment.id)}>✕</button>}
                    </div>
                    <CommentContent value={comment.content} className="aud-comment-text rc-comment-body" />
                  </div>
                ))}
              </div>
              <div className="aud-comment-compose">
                <RichCommentInput
                  collapsible
                  placeholder="Add a comment…"
                  rows={3}
                  value={commentDraft}
                  onChange={setCommentDraft}
                  onSubmit={(payload) => handleCreateComment(payload)}
                  users={users}
                  teams={teams}
                  mentionScopeLabel="audit"
                  submitLabel="Post"
                />
              </div>
            </div>
          )}
        </SheetPrimitives.SheetBody>
      </SheetPrimitives.SheetContent>
    </SheetPrimitives.Sheet>
  );
}

function Audits({audits, setAudits, showToast, currentUser, addNotification, teams = []}){
  // State
  const [viewMode, setViewMode] = useState("kanban");
  const [stageFilter, setStageFilter] = useState("");
  const [drawerAuditId, setDrawerAuditId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState({name:'', auditTypeId:'', contactId:'', languageId:'', status:'Questionnaire', ownerId:'', taskTemplateCategoryId:''});
  const [auditTypes, setAuditTypes] = useState([]);
  const [taskTemplateCategories, setTaskTemplateCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [users, setUsers] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [newLangInput, setNewLangInput] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [newSubitemName, setNewSubitemName] = useState({});
  const [editingSubCell, setEditingSubCell] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusValues, setStatusValues] = useState([]);

  // Load meta
  useEffect(()=>{
    apiClient.getAuditTypes().then(d=>setAuditTypes(d)).catch(()=>{});
    apiClient.getTaskTemplateCategories().then(d=>setTaskTemplateCategories(d.map(mapTaskTemplateCategory))).catch(()=>{});
    apiClient.getLanguages().then(d=>setLanguages(d)).catch(()=>{});
    apiClient.getUsers().then(d=>setUsers(d.users||d||[])).catch(()=>{});
    apiClient.getStatusValues('audit').then(d=>setStatusValues(d||[])).catch(()=>{});
  },[]);

  // Stages derived from StatusValue table; fallback to AUDIT_STATUSES if not loaded yet
  const auditStages = useMemo(()=>
    statusValues.length > 0
      ? statusValues.map(sv=>sv.name)
      : AUDIT_STATUSES,
  [statusValues]);

  // Stage color: prefer StatusValue color, fallback to statusColor() helper
  const stageColorMap = useMemo(()=>{
    const map = {};
    statusValues.forEach(sv=>{ map[sv.name]=sv.color; });
    return map;
  },[statusValues]);

  function stageColor(status) {
    return stageColorMap[status] || statusColor(status);
  }

  const filteredAudits = useMemo(
    ()=>stageFilter ? audits.filter(a=>a.status===stageFilter) : audits,
    [audits, stageFilter]
  );

  // Group audits by type
  const grouped = useMemo(()=>{
    const map = {};
    filteredAudits.forEach(a=>{
      const key = a.type || 'Other';
      if(!map[key]) map[key]=[];
      map[key].push(a);
    });
    return Object.entries(map).sort(([a],[b])=>a.localeCompare(b));
  },[filteredAudits]);

  // Init all groups expanded
  useEffect(()=>{
    const init = {};
    grouped.forEach(([key])=>{ if(expandedGroups[key]===undefined) init[key]=true; });
    if(Object.keys(init).length) setExpandedGroups(prev=>({...init,...prev}));
  },[grouped]);// eslint-disable-line

  function upsertAudit(raw){
    const mapped = mapAudit(raw);
    setAudits(prev=>{
      const idx = prev.findIndex(a=>a.id===mapped.id);
      if(idx===-1) return [mapped,...prev];
      const next=[...prev]; next[idx]=mapped; return next;
    });
    return mapped;
  }

  const activeAudit = drawerAuditId ? audits.find(a=>a.id===drawerAuditId) : null;

  const stats = useMemo(()=>({
    total: audits.length,
    inProgress: audits.filter(a=>!["Done","General Task"].includes(a.status)).length,
    completed: audits.filter(a=>a.status==='Done').length,
    awaiting: audits.filter(a=>a.status==='Questionnaire').length,
  }),[audits]);

  const stageCounts = useMemo(
    ()=>auditStages.reduce((acc, status)=>{
      acc[status]=audits.filter(a=>a.status===status).length;
      return acc;
    },{}),
    [audits, auditStages]
  );

  // Close dropdowns on outside click
  useEffect(()=>{
    const handler = ()=>setOpenDropdown(null);
    document.addEventListener('click', handler);
    return ()=>document.removeEventListener('click', handler);
  },[]);

  // ── Handlers ──
  async function handleCreate(){
    if(!newForm.name.trim()) return;
    setSaving(true);
    try {
      const raw = await apiClient.createAudit({
        name: newForm.name.trim(),
        auditTypeId: newForm.auditTypeId||null,
        contactId: newForm.contactId || currentUser?.id || null,
        languageId: newForm.languageId||null,
        status: newForm.status||'Questionnaire',
        ownerId: newForm.ownerId || currentUser?.id || null,
        taskTemplateCategoryId: newForm.taskTemplateCategoryId||null,
      });
      const created = upsertAudit(raw);
      setDrawerAuditId(created.id);
      setShowNewModal(false);
      setNewForm({name:'',auditTypeId:'',contactId:'',languageId:'',status:'Questionnaire',ownerId:'',taskTemplateCategoryId:''});
      showToast('Audit created','success');
    } catch(e){
      showToast(e.message||'Failed to create audit','error');
    } finally { setSaving(false); }
  }

  async function handleFieldSave(auditId, field, value){
    try {
      const raw = await apiClient.updateAudit(auditId, {[field]: value||null});
      upsertAudit(raw);
    } catch(e){ showToast(e.message||'Save failed','error'); }
    setEditingCell(null);
  }

  async function handleStatusChange(auditId, newStatus){
    setOpenDropdown(null);
    try {
      const audit = audits.find(a=>a.id===auditId);
      const newHistory = [...(audit?.stageHistory||[]), {stage:newStatus, enteredAt:new Date().toISOString(), by:currentUser?.name||''}];
      const raw = await apiClient.updateAudit(auditId, {status:newStatus, stageHistory:newHistory});
      upsertAudit(raw);
    } catch(e){ showToast(e.message||'Failed to update status','error'); }
  }

  async function handleLangChange(auditId, languageId){
    setOpenDropdown(null);
    try {
      const raw = await apiClient.updateAudit(auditId, {languageId: languageId||null});
      upsertAudit(raw);
    } catch(e){ showToast(e.message||'Failed to update language','error'); }
  }

  async function handleAddLanguage(auditId){
    if(!newLangInput.trim()) return;
    try {
      const lang = await apiClient.createLanguage(newLangInput.trim());
      setLanguages(prev=>[...prev,lang].sort((a,b)=>a.name.localeCompare(b.name)));
      if(auditId) {
        const raw = await apiClient.updateAudit(auditId, {languageId: lang.id});
        upsertAudit(raw);
      }
      setNewLangInput('');
      setOpenDropdown(null);
    } catch(e){ showToast(e.message||'Failed to add language','error'); }
  }

  async function handleDelete(auditId){
    if(!window.confirm('Delete this audit? This cannot be undone.')) return;
    try {
      await apiClient.deleteAudit(auditId);
      setAudits(prev=>prev.filter(a=>a.id!==auditId));
      if(drawerAuditId===auditId) setDrawerAuditId(null);
      showToast('Audit deleted','success');
    } catch(e){ showToast(e.message||'Failed to delete audit','error'); }
  }

  async function handleCreateSubitem(auditId){
    const name = (newSubitemName[auditId]||'').trim();
    if(!name) return;
    try {
      const raw = await apiClient.createAuditSubitem(auditId, {name});
      upsertAudit(raw);
      setNewSubitemName(prev=>({...prev,[auditId]:''}));
    } catch(e){ showToast(e.message||'Failed to add person','error'); }
  }

  async function handleSubitemToggle(auditId, subitemId, field, value){
    try {
      const raw = await apiClient.updateAuditSubitem(auditId, subitemId, {[field]: value});
      upsertAudit(raw);
    } catch(e){ showToast(e.message||'Failed to update','error'); }
  }

  async function handleSubitemFieldSave(auditId, subitemId, field, value){
    try {
      const raw = await apiClient.updateAuditSubitem(auditId, subitemId, {[field]: value||null});
      upsertAudit(raw);
    } catch(e){ showToast(e.message||'Failed to save','error'); }
    setEditingSubCell(null);
  }

  async function handleDeleteSubitem(auditId, subitemId){
    try {
      const raw = await apiClient.deleteAuditSubitem(auditId, subitemId);
      upsertAudit(raw);
    } catch(e){ showToast(e.message||'Failed to delete','error'); }
  }

  async function handleDeleteComment(auditId, commentId){
    try {
      const raw = await apiClient.deleteAuditComment(auditId, commentId);
      upsertAudit(raw);
    } catch(e){ showToast(e.message||'Failed to delete comment','error'); }
  }

  async function handleAuditDrawerAction(auditId, field, value){
    if (field === "__createComment") {
      const payload = typeof value === 'object' ? value : { content: value };
      const raw = await apiClient.createAuditComment(auditId, payload);
      return upsertAudit(raw);
    }
    const raw = await apiClient.updateAudit(auditId, { [field]: value || null });
    return upsertAudit(raw);
  }

  async function handleGenerateAuditTasks(auditId, categoryId, replaceExisting=false){
    try {
      const raw = await apiClient.generateAuditTasks(auditId, { categoryId, replaceExisting });
      upsertAudit(raw);
      showToast(replaceExisting ? 'Tasks regenerated' : 'Tasks generated', 'success');
    } catch (e) {
      showToast(e.response?.data?.error || e.message || 'Failed to generate tasks', 'error');
    }
  }

  async function handleCreateAuditTask(auditId, taskData){
    try {
      const raw = await apiClient.createAuditTask(auditId, taskData);
      upsertAudit(raw);
    } catch (e) {
      showToast(e.message || 'Failed to create task', 'error');
    }
  }

  async function handleUpdateAuditTask(auditId, taskId, taskData){
    try {
      const raw = await apiClient.updateAuditTask(auditId, taskId, taskData);
      upsertAudit(raw);
      // Notify when the current user is assigned an audit task (mirrors scoping assignTask pattern)
      if (taskData.assigneeId && taskData.assigneeId === currentUser?.id && addNotification) {
        const task = (raw.auditTasks || []).find(t => t.id === taskId);
        addNotification({
          id: uid(),
          type: "assignment",
          text: `You've been assigned an audit task${task ? `: "${task.title}"` : ""}`,
          ts: new Date().toISOString(),
          read: false,
        });
      }
    } catch (e) {
      showToast(e.message || 'Failed to update task', 'error');
    }
  }

  async function handleDeleteAuditTask(auditId, taskId){
    try {
      const raw = await apiClient.deleteAuditTask(auditId, taskId);
      upsertAudit(raw);
    } catch (e) {
      showToast(e.message || 'Failed to delete task', 'error');
    }
  }

  async function handleCreateAuditTaskComment(auditId, taskId, payload){
    try {
      const body = typeof payload === 'object' ? payload : { content: payload };
      const raw = await apiClient.createAuditTaskComment(auditId, taskId, body);
      upsertAudit(raw);
    } catch (e) {
      showToast(e.message || 'Failed to add task comment', 'error');
      throw e;
    }
  }

  function toggleGroup(key){ setExpandedGroups(p=>({...p,[key]:!p[key]})); }
  function toggleRow(id){ setExpandedRows(p=>({...p,[id]:!p[id]})); }

  // ── Inner cell components ──
  function InlineCell({auditId, field, value, placeholder}){
    const isEditing = editingCell?.auditId===auditId && editingCell?.field===field;
    if(isEditing) return (
      <input className="bg-[#070e20] border border-rc-accent rounded px-2 py-1 text-[12px] text-rc-text-primary outline-none w-full box-border font-sans" autoFocus defaultValue={value||''}
        onBlur={e=>handleFieldSave(auditId,field,e.target.value)}
        onKeyDown={e=>{
          if(e.key==='Enter') handleFieldSave(auditId,field,e.target.value);
          if(e.key==='Escape') setEditingCell(null);
        }}/>
    );
    return <span className={value ? "text-[12px] text-rc-text-primary cursor-pointer block whitespace-nowrap overflow-hidden text-ellipsis hover:text-[#93c5fd]" : "text-[12px] text-[#2a3f60] cursor-pointer hover:text-[#4a6a96]"}
      onClick={()=>setEditingCell({auditId,field})}>{value||placeholder||'—'}</span>;
  }

  function StatusCell({audit}){
    const isOpen = openDropdown?.auditId===audit.id && openDropdown?.field==='status';
    return (
      <div className="relative cursor-pointer" onClick={e=>{e.stopPropagation(); setOpenDropdown(isOpen?null:{auditId:audit.id,field:'status'});}}>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap" style={{background:stageColor(audit.status)+'22',color:stageColor(audit.status),border:`1px solid ${stageColor(audit.status)}44`}}>{audit.status}</span>
        {isOpen && <div className="absolute top-[calc(100%+4px)] left-0 bg-[#0e1c38] border border-rc-border rounded-lg z-[50] min-w-[150px] py-1 shadow-[0_8px_24px_rgba(0,0,0,0.5)]">
          {auditStages.map(s=><div key={s} className="px-3 py-1.5 text-[12px] text-rc-text-secondary cursor-pointer transition-colors hover:bg-rc-border hover:text-rc-text-primary" onClick={e=>{e.stopPropagation();handleStatusChange(audit.id,s);}}>
            <span style={{color:stageColor(s)}}>{s}</span>
          </div>)}
        </div>}
      </div>
    );
  }

  function LangCell({audit}){
    const isOpen = openDropdown?.auditId===audit.id && openDropdown?.field==='lang';
    const langName = audit.language||'';
    return (
      <div className="relative cursor-pointer" onClick={e=>{e.stopPropagation(); setOpenDropdown(isOpen?null:{auditId:audit.id,field:'lang'});}}>
        {langName ? <span className="inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[rgba(100,116,139,0.15)] text-[#94a3b8] border border-[rgba(100,116,139,0.25)] whitespace-nowrap">{langName}</span> : <span className="text-[12px] text-[#2a3f60] cursor-pointer hover:text-[#4a6a96]">—</span>}
        {isOpen && <div className="absolute top-[calc(100%+4px)] left-0 bg-[#0e1c38] border border-rc-border rounded-lg z-[50] min-w-[180px] py-1 shadow-[0_8px_24px_rgba(0,0,0,0.5)] max-h-[260px] overflow-y-auto" onClick={e=>e.stopPropagation()}>
          {languages.map(l=><div key={l.id} data-active={audit.languageId===l.id} className="px-3 py-1.5 text-[12px] text-rc-text-secondary cursor-pointer transition-colors hover:bg-rc-border hover:text-rc-text-primary data-[active=true]:!text-rc-accent-strong data-[active=true]:font-semibold"
            onClick={()=>handleLangChange(audit.id,l.id)}>{l.name}</div>)}
          <div className="flex gap-1 px-2 py-1.5 border-t border-rc-border mt-1">
            <input className="flex-1 bg-[#070e20] border border-rc-border rounded px-2 py-1 text-rc-text-primary text-[11px] outline-none font-sans" placeholder="Add language…" value={newLangInput}
              onChange={e=>setNewLangInput(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter')handleAddLanguage(audit.id);}}/>
            <button className="bg-[rgba(59,127,250,0.15)] border border-[rgba(59,127,250,0.3)] text-rc-accent-strong rounded px-2 py-1 text-[12px] cursor-pointer transition-colors hover:bg-[rgba(59,127,250,0.25)]" onClick={()=>handleAddLanguage(audit.id)}>+</button>
          </div>
        </div>}
      </div>
    );
  }

  function ContactCell({audit}){
    const isOpen = openDropdown?.auditId===audit.id && openDropdown?.field==='contact';
    const [search,setSearch]=useState('');
    const filteredUsers = users.filter(u=>u.name.toLowerCase().includes(search.toLowerCase()));
    return (
      <div className="relative cursor-pointer" onClick={e=>{e.stopPropagation(); setOpenDropdown(isOpen?null:{auditId:audit.id,field:'contact'});}}>
        {audit.contact ? <span className="text-[12px] text-rc-text-primary cursor-pointer block whitespace-nowrap overflow-hidden text-ellipsis hover:text-[#93c5fd]">{audit.contact}</span> : <span className="text-[12px] text-[#2a3f60] cursor-pointer hover:text-[#4a6a96]">—</span>}
        {isOpen && <div className="absolute top-[calc(100%+4px)] left-0 bg-[#0e1c38] border border-rc-border rounded-lg z-[50] min-w-[180px] py-1 shadow-[0_8px_24px_rgba(0,0,0,0.5)] max-h-[260px] overflow-y-auto" onClick={e=>e.stopPropagation()}>
          <input className="flex-1 bg-[#070e20] border border-rc-border rounded px-2 py-1 text-rc-text-primary text-[11px] outline-none font-sans" placeholder="Search user…" value={search}
            onChange={e=>setSearch(e.target.value)} autoFocus/>
          <div style={{maxHeight:200,overflowY:'auto'}}>
            <div className="px-3 py-1.5 text-[12px] text-rc-text-secondary cursor-pointer transition-colors hover:bg-rc-border hover:text-rc-text-primary" onClick={()=>{handleFieldSave(audit.id,'contactId',null);setOpenDropdown(null);}}>— None —</div>
            {filteredUsers.map(u=><div key={u.id} data-active={audit.contactId===u.id} className="px-3 py-1.5 text-[12px] text-rc-text-secondary cursor-pointer transition-colors hover:bg-rc-border hover:text-rc-text-primary data-[active=true]:!text-rc-accent-strong data-[active=true]:font-semibold"
              onClick={()=>{handleFieldSave(audit.id,'contactId',u.id);setOpenDropdown(null);}}>{u.name}</div>)}
          </div>
        </div>}
      </div>
    );
  }

  function SubCell({auditId, subitemId, field, value, placeholder}){
    const isEditing = editingSubCell?.subitemId===subitemId && editingSubCell?.field===field;
    if(isEditing) return (
      <input className="bg-[#070e20] border border-rc-accent rounded px-2 py-1 text-[12px] text-rc-text-primary outline-none w-full box-border font-sans" autoFocus defaultValue={value||''}
        onBlur={e=>handleSubitemFieldSave(auditId,subitemId,field,e.target.value)}
        onKeyDown={e=>{
          if(e.key==='Enter') handleSubitemFieldSave(auditId,subitemId,field,e.target.value);
          if(e.key==='Escape') setEditingSubCell(null);
        }}/>
    );
    return <span className={value ? "text-[12px] text-rc-text-primary cursor-pointer block whitespace-nowrap overflow-hidden text-ellipsis hover:text-[#93c5fd]" : "text-[12px] text-[#2a3f60] cursor-pointer hover:text-[#4a6a96]"}
      onClick={()=>setEditingSubCell({subitemId,field})}>{value||placeholder||'—'}</span>;
  }

  function SubDateCell({auditId, subitemId, field, value}){
    const isEditing = editingSubCell?.subitemId===subitemId && editingSubCell?.field===field;
    if(isEditing) return (
      <input type="date" className="bg-[#070e20] border border-rc-accent rounded px-2 py-1 text-[12px] text-rc-text-primary outline-none w-full box-border font-sans" autoFocus defaultValue={value||''}
        onBlur={e=>handleSubitemFieldSave(auditId,subitemId,field,e.target.value)}
        onKeyDown={e=>{
          if(e.key==='Enter') handleSubitemFieldSave(auditId,subitemId,field,e.target.value);
          if(e.key==='Escape') setEditingSubCell(null);
        }}/>
    );
    return <span className={value ? "text-[12px] text-rc-text-primary cursor-pointer block whitespace-nowrap overflow-hidden text-ellipsis hover:text-[#93c5fd]" : "text-[12px] text-[#2a3f60] cursor-pointer hover:text-[#4a6a96]"}
      onClick={()=>setEditingSubCell({subitemId,field})}>{value||'—'}</span>;
  }

  const COLS = [
    {key:'name',       label:'Name',        w:'240px'},
    {key:'contact',    label:'Contact',     w:'140px'},
    {key:'status',     label:'Status',      w:'140px'},
    {key:'language',   label:'Language',    w:'120px'},
    {key:'driveLink',  label:'Drive Link',  w:'120px'},
    {key:'lastUpdate', label:'Last Update', w:'110px'},
    {key:'estimator',  label:'Estimator',   w:'110px'},
    {key:'contract',   label:'Contract',    w:'110px'},
    {key:'transcripts',label:'Transcripts', w:'110px'},
  ];
  const SUB_COLS = [
    {key:'name',                  label:'Name',          w:'180px'},
    {key:'email',                 label:'Email',         w:'180px'},
    {key:'primaryPosition',       label:'Primary Role',  w:'140px'},
    {key:'secondaryPosition',     label:'Secondary Role',w:'140px'},
    {key:'questionnaireSent',     label:'Q. Sent',       w:'80px', bool:true},
    {key:'aktivTrackGuideSent',   label:'AT Guide',      w:'80px', bool:true},
    {key:'atSent',                label:'AT Sent',       w:'80px', bool:true},
    {key:'questionnaireDeadline', label:'Q. Deadline',   w:'110px', date:true},
    {key:'aktivTrakReturn',       label:'AT Return',     w:'110px', date:true},
    {key:'interview',             label:'Interview',     w:'110px', date:true},
    {key:'interviewConfirmation', label:'Confirmation',  w:'130px'},
  ];

  return (
    <div className="fade flex flex-col">
      <div className="pg-top">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="pg-title">Audits</div>
            <div className="pg-sub">Standalone audit tracking with the old board hierarchy and the current editable table workflow</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-1 p-1 bg-[rgba(14,28,56,0.72)] border border-rc-border rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.18)]">
              <button data-active={viewMode==="kanban"} onClick={()=>setViewMode("kanban")} className="bg-transparent border-0 rounded-[9px] px-3.5 py-2 text-xs font-bold cursor-pointer transition-colors font-sans text-[#6889b4] hover:text-rc-text-primary hover:bg-[rgba(59,127,250,0.08)] data-[active=true]:text-rc-text-primary data-[active=true]:bg-gradient-to-b data-[active=true]:from-[rgba(59,127,250,0.2)] data-[active=true]:to-[rgba(59,127,250,0.1)] data-[active=true]:shadow-[inset_0_0_0_1px_rgba(96,165,250,0.22)]">⊞ Kanban</button>
              <button data-active={viewMode==="table"} onClick={()=>setViewMode("table")} className="bg-transparent border-0 rounded-[9px] px-3.5 py-2 text-xs font-bold cursor-pointer transition-colors font-sans text-[#6889b4] hover:text-rc-text-primary hover:bg-[rgba(59,127,250,0.08)] data-[active=true]:text-rc-text-primary data-[active=true]:bg-gradient-to-b data-[active=true]:from-[rgba(59,127,250,0.2)] data-[active=true]:to-[rgba(59,127,250,0.1)] data-[active=true]:shadow-[inset_0_0_0_1px_rgba(96,165,250,0.22)]">☰ Table</button>
            </div>
            <button className="wr-gen-btn" onClick={()=>setShowNewModal(true)}>+ New Audit</button>
          </div>
        </div>
      </div>

      <div className="pg-body flex flex-col gap-[18px]">
        <div className="krow">
          <div className="kpi"><div className="kpi-lbl">Total Audits</div><div className="kpi-val">{stats.total}</div></div>
          <div className="kpi"><div className="kpi-lbl">In Progress</div><div className="kpi-val" style={{color:C.blue}}>{stats.inProgress}</div></div>
          <div className="kpi"><div className="kpi-lbl">Completed</div><div className="kpi-val" style={{color:C.green}}>{stats.completed}</div></div>
          <div className="kpi"><div className="kpi-lbl">Awaiting Action</div><div className="kpi-val" style={{color:C.yellow}}>{stats.awaiting}</div></div>
        </div>

        {viewMode==="kanban" && (
          <>
            <div className="flex gap-1.5 py-4 overflow-x-auto">
              <div data-active={stageFilter===""} onClick={()=>setStageFilter("")} className="flex-1 min-w-[140px] bg-rc-surface border border-rc-border rounded-[10px] px-3 py-2.5 text-center cursor-pointer transition-colors relative hover:border-[#1e3a5f] hover:bg-[#0d1a30] data-[active=true]:border-rc-accent data-[active=true]:bg-[rgba(59,127,250,0.06)]">
                <div className="text-[11px] font-bold text-rc-text-secondary uppercase tracking-[0.04em]">All</div>
                <div className="text-[22px] font-extrabold text-rc-text-primary my-1">{audits.length}</div>
                <div className="text-[9px] text-[#4a6a96]">all stages</div>
              </div>
              {auditStages.map(s=>{
                const cnt=stageCounts[s]||0;
                const d=AUDIT_STAGE_DEPT[s];
                return (
                  <div key={s} data-active={stageFilter===s} onClick={()=>setStageFilter(stageFilter===s?"":s)} className="flex-1 min-w-[140px] bg-rc-surface border border-rc-border rounded-[10px] px-3 py-2.5 text-center cursor-pointer transition-colors relative hover:border-[#1e3a5f] hover:bg-[#0d1a30] data-[active=true]:border-rc-accent data-[active=true]:bg-[rgba(59,127,250,0.06)]">
                    <div className="text-[11px] font-bold uppercase tracking-[0.04em]" style={{color:stageColor(s)}}>{s}</div>
                    <div className="text-[22px] font-extrabold text-rc-text-primary my-1">{cnt}</div>
                    <div className="text-[9px] text-[#4a6a96]">{d?.dept||"—"}</div>
                    <div className="h-[3px] rounded mt-1.5 bg-[rgba(143,164,200,0.1)] overflow-hidden"><div className="h-full rounded transition-[width] duration-300" style={{width:`${audits.length>0?Math.round(cnt/audits.length*100):0}%`,background:stageColor(s)}}/></div>
                  </div>
                );
              })}
            </div>

            <div className="grid gap-3" style={{gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))"}}>
              {filteredAudits.map(a=>{
                const tasks=a.auditTasks||[];
                const done=tasks.filter(t=>t.status==="Done").length;
                const pct=tasks.length>0?Math.round(done/tasks.length*100):0;
                const col=stageColor(a.status);
                const latestNote=(a.notes||[])[(a.notes||[]).length-1];
                return (
                  <div key={a.id} onClick={()=>setDrawerAuditId(a.id)} style={{"--aud-accent":col}} className="relative bg-rc-surface border border-rc-border rounded-xl p-4 cursor-pointer overflow-hidden transition-all hover:border-[#1e3a5f] hover:-translate-y-px hover:shadow-[0_4px_16px_rgba(0,0,0,0.3)] before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:rounded-l-[10px] before:bg-[var(--aud-accent,#3b7ffa)]">
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="text-sm font-bold text-rc-text-primary">{a.name}</div>
                      <span className="text-[9px] font-semibold text-[#4a6a96] bg-[#0e1c38] border border-rc-border px-2 py-0.5 rounded-[10px] uppercase tracking-[0.04em]">{a.type}</span>
                    </div>
                    <div className="flex gap-3.5 text-[11px] text-rc-text-secondary mb-2.5 flex-wrap [&_strong]:text-rc-text-primary [&_strong]:mr-0.5">
                      <span><strong>Status:</strong><span style={{color:col}}> {a.status}</span></span>
                      {a.contact&&<span><strong>Contact:</strong> {a.contact}</span>}
                      {a.language&&<span><strong>Lang:</strong> {a.language}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-[5px] bg-[rgba(143,164,200,0.08)] rounded overflow-hidden"><div className="h-full rounded transition-[width] duration-300" style={{width:`${pct}%`,background:pct>=100?C.green:pct>=50?C.blue:col}}/></div>
                      <span className="text-[10px] font-bold min-w-[28px] text-right" style={{color:pct>=100?C.green:pct>=50?C.blue:col}}>{done}/{tasks.length}</span>
                    </div>
                    {latestNote&&<div className="text-[11px] text-[#4a6a96] mt-2 italic whitespace-nowrap overflow-hidden text-ellipsis">{latestNote.text}</div>}
                    <div className="flex items-center justify-between gap-2.5 mt-3 pt-2.5 border-t border-[rgba(20,33,61,0.7)] text-[10px] text-[#6889b4] [&_a]:text-rc-accent-strong [&_a]:no-underline [&_a]:font-bold hover:[&_a]:underline">
                      <span>{a.lastUpdate||"No updates"}</span>
                      {a.driveLink&&<a href={a.driveLink} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}>Open Drive</a>}
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredAudits.length===0 && (
              <div className="p-16 text-center text-[#4a6a96] text-sm">
                <p>No audits yet. Click <strong>+ New Audit</strong> to get started.</p>
              </div>
            )}

          </>
        )}

        {viewMode==="table" && (
          <div className="flex relative mt-4">
        <div style={{flex:1,minWidth:0,overflowX:'auto'}}>
          <div className="flex items-center pb-1 border-b-2 border-rc-border sticky top-0 bg-[var(--bg-page,#060d1f)] z-[2]">
            <div className="text-[10px] font-bold text-[#4a6a96] uppercase tracking-[0.04em] px-2.5 py-1.5 shrink-0 select-none" style={{width:32,minWidth:32}}/>
            {COLS.map(c=><div key={c.key} className="text-[10px] font-bold text-[#4a6a96] uppercase tracking-[0.04em] px-2.5 py-1.5 shrink-0 select-none" style={{width:c.w,minWidth:c.w}}>{c.label}</div>)}
            <div className="text-[10px] font-bold text-[#4a6a96] uppercase tracking-[0.04em] px-2.5 py-1.5 shrink-0 select-none" style={{width:32,minWidth:32}}/>
          </div>

          {grouped.map(([typeName,rows])=>(
            <div key={typeName} className="">
              <div className="flex items-center gap-2 px-2.5 py-2 bg-[#0d1a30] border-y border-rc-border cursor-pointer transition-colors mt-2 hover:bg-rc-surface-hover" onClick={()=>toggleGroup(typeName)}>
                <span className="bg-transparent border-0 text-[#4a6a96] cursor-pointer text-[10px] px-1 leading-none transition-colors hover:text-rc-text-secondary">{expandedGroups[typeName]?'▼':'▶'}</span>
                <span className="text-[13px] font-bold text-rc-text-primary flex-1">{typeName}</span>
                <span className="bg-[rgba(59,127,250,0.15)] text-rc-accent-strong border border-[rgba(59,127,250,0.25)] rounded-[10px] text-[10px] font-bold px-2 py-px">{rows.length}</span>
              </div>

              {expandedGroups[typeName] && rows.map(audit=>(
                <div key={audit.id} className="border-b border-rc-surface-hover">
                  <div data-expanded={!!expandedRows[audit.id]} className="flex items-center min-h-[40px] transition-colors hover:bg-rc-surface data-[expanded=true]:bg-rc-surface">
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:32,minWidth:32}}>
                      <button className="bg-transparent border-0 text-[#4a6a96] cursor-pointer text-[10px] px-1 leading-none transition-colors hover:text-rc-text-secondary" onClick={e=>{e.stopPropagation();toggleRow(audit.id);}}>
                        {(audit.subitems?.length>0||expandedRows[audit.id]) ? (expandedRows[audit.id]?'▼':'▶') : ''}
                      </button>
                    </div>
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:COLS[0].w,minWidth:COLS[0].w}}>
                      <span className="text-[12px] font-semibold text-rc-accent-strong cursor-pointer flex-1 hover:text-[#93c5fd] hover:underline" onClick={()=>setDrawerAuditId(audit.id)}>{audit.name}</span>
                      {audit.subitems?.length>0 && <span className="text-[9px] text-[#4a6a96] ml-1.5 whitespace-nowrap">{audit.subitems.length} person{audit.subitems.length!==1?'s':''}</span>}
                    </div>
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:COLS[1].w,minWidth:COLS[1].w}}><ContactCell audit={audit}/></div>
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:COLS[2].w,minWidth:COLS[2].w}}><StatusCell audit={audit}/></div>
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:COLS[3].w,minWidth:COLS[3].w}}><LangCell audit={audit}/></div>
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:COLS[4].w,minWidth:COLS[4].w}}>
                      {audit.driveLink
                        ? <a className="text-[11px] text-rc-accent-strong no-underline whitespace-nowrap hover:underline" href={audit.driveLink} target="_blank" rel="noreferrer">↗ Drive</a>
                        : <InlineCell auditId={audit.id} field="driveLink" value={audit.driveLink} placeholder="—"/>}
                    </div>
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:COLS[5].w,minWidth:COLS[5].w}}>
                      <InlineCell auditId={audit.id} field="lastUpdate" value={audit.lastUpdate} placeholder="—"/>
                    </div>
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:COLS[6].w,minWidth:COLS[6].w}}>
                      <InlineCell auditId={audit.id} field="estimator" value={audit.estimator} placeholder="—"/>
                    </div>
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:COLS[7].w,minWidth:COLS[7].w}}>
                      <InlineCell auditId={audit.id} field="contract" value={audit.contract} placeholder="—"/>
                    </div>
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:COLS[8].w,minWidth:COLS[8].w}}>
                      <InlineCell auditId={audit.id} field="transcripts" value={audit.transcripts} placeholder="—"/>
                    </div>
                    <div className="flex items-center px-2.5 py-1.5 shrink-0 min-h-[38px] relative" style={{width:32,minWidth:32}}>
                      <button className="bg-transparent border-0 text-[#2a3f60] cursor-pointer text-[11px] px-1.5 py-0.5 rounded transition-colors hover:bg-[rgba(239,68,68,0.12)] hover:text-[#f87171]" onClick={()=>handleDelete(audit.id)} title="Delete">✕</button>
                    </div>
                  </div>

                  {expandedRows[audit.id] && (
                    <div className="bg-[#070e20] border-b-2 border-rc-border">
                      <div className="flex items-center py-1 border-b border-rc-border">
                        <div className="text-[9px] font-bold text-[#2a3f60] uppercase tracking-[0.03em] px-2 py-1 shrink-0" style={{width:48,minWidth:48}}/>
                        {SUB_COLS.map(c=><div key={c.key} className="text-[9px] font-bold text-[#2a3f60] uppercase tracking-[0.03em] px-2 py-1 shrink-0" style={{width:c.w,minWidth:c.w}}>{c.label}</div>)}
                        <div className="text-[9px] font-bold text-[#2a3f60] uppercase tracking-[0.03em] px-2 py-1 shrink-0" style={{width:32,minWidth:32}}/>
                      </div>
                      {(audit.subitems||[]).map(sub=>(
                        <div key={sub.id} className="flex items-center min-h-[34px] border-b border-[#0d1a30] hover:bg-rc-surface">
                          <div className="flex items-center px-2 py-1 shrink-0" style={{width:48,minWidth:48}}/>
                          <div className="flex items-center px-2 py-1 shrink-0" style={{width:SUB_COLS[0].w,minWidth:SUB_COLS[0].w}}>
                            <SubCell auditId={audit.id} subitemId={sub.id} field="name" value={sub.name}/>
                          </div>
                          <div className="flex items-center px-2 py-1 shrink-0" style={{width:SUB_COLS[1].w,minWidth:SUB_COLS[1].w}}>
                            <SubCell auditId={audit.id} subitemId={sub.id} field="email" value={sub.email}/>
                          </div>
                          <div className="flex items-center px-2 py-1 shrink-0" style={{width:SUB_COLS[2].w,minWidth:SUB_COLS[2].w}}>
                            <SubCell auditId={audit.id} subitemId={sub.id} field="primaryPosition" value={sub.primaryPosition}/>
                          </div>
                          <div className="flex items-center px-2 py-1 shrink-0" style={{width:SUB_COLS[3].w,minWidth:SUB_COLS[3].w}}>
                            <SubCell auditId={audit.id} subitemId={sub.id} field="secondaryPosition" value={sub.secondaryPosition}/>
                          </div>
                          {['questionnaireSent','aktivTrackGuideSent','atSent'].map(f=>(
                            <div key={f} className="flex items-center justify-center px-2 py-1 shrink-0 [&>input[type=checkbox]]:w-3.5 [&>input[type=checkbox]]:h-3.5 [&>input[type=checkbox]]:cursor-pointer [&>input[type=checkbox]]:accent-rc-accent" style={{width:'80px',minWidth:'80px'}}>
                              <input type="checkbox" checked={!!sub[f]}
                                onChange={e=>handleSubitemToggle(audit.id,sub.id,f,e.target.checked)}/>
                            </div>
                          ))}
                          <div className="flex items-center px-2 py-1 shrink-0" style={{width:SUB_COLS[7].w,minWidth:SUB_COLS[7].w}}>
                            <SubDateCell auditId={audit.id} subitemId={sub.id} field="questionnaireDeadline" value={sub.questionnaireDeadline}/>
                          </div>
                          <div className="flex items-center px-2 py-1 shrink-0" style={{width:SUB_COLS[8].w,minWidth:SUB_COLS[8].w}}>
                            <SubDateCell auditId={audit.id} subitemId={sub.id} field="aktivTrakReturn" value={sub.aktivTrakReturn}/>
                          </div>
                          <div className="flex items-center px-2 py-1 shrink-0" style={{width:SUB_COLS[9].w,minWidth:SUB_COLS[9].w}}>
                            <SubDateCell auditId={audit.id} subitemId={sub.id} field="interview" value={sub.interview}/>
                          </div>
                          <div className="flex items-center px-2 py-1 shrink-0" style={{width:SUB_COLS[10].w,minWidth:SUB_COLS[10].w}}>
                            <SubCell auditId={audit.id} subitemId={sub.id} field="interviewConfirmation" value={sub.interviewConfirmation}/>
                          </div>
                          <div className="flex items-center px-2 py-1 shrink-0" style={{width:32,minWidth:32}}>
                            <button className="bg-transparent border-0 text-[#2a3f60] cursor-pointer text-[11px] px-1.5 py-0.5 rounded transition-colors hover:bg-[rgba(239,68,68,0.12)] hover:text-[#f87171]" onClick={()=>handleDeleteSubitem(audit.id,sub.id)}>✕</button>
                          </div>
                        </div>
                      ))}
                      <div className="flex items-center py-1">
                        <div className="flex items-center px-2 py-1 shrink-0" style={{width:48,minWidth:48}}/>
                        <div className="flex items-center px-2 py-1 shrink-0" style={{flex:1}}>
                          <input className="bg-transparent border-0 border-b border-rc-border text-[#4a6a96] text-[11px] px-2 py-1 outline-none w-full font-sans focus:border-b-rc-accent focus:text-rc-text-primary placeholder:text-[#1e3a5f]" placeholder="+ Add person…"
                            value={newSubitemName[audit.id]||''}
                            onChange={e=>setNewSubitemName(p=>({...p,[audit.id]:e.target.value}))}
                            onKeyDown={e=>{if(e.key==='Enter')handleCreateSubitem(audit.id);}}/>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <div className="px-2.5 pt-1.5 pb-2.5 pl-[42px]">
                <span className="text-[11px] text-rc-accent cursor-pointer opacity-70 hover:opacity-100 hover:underline" onClick={()=>{
                  const typeObj=auditTypes.find(t=>t.name===typeName);
                  setNewForm(p=>({...p,auditTypeId:typeObj?.id||'',name:''}));
                  setShowNewModal(true);
                }}>+ Add Audit</span>
              </div>
            </div>
          ))}

          {filteredAudits.length===0 && (
            <div className="p-16 text-center text-[#4a6a96] text-sm">
              <p>No audits yet. Click <strong>+ New Audit</strong> to get started.</p>
            </div>
          )}
        </div>
          </div>
        )}
      </div>

      {activeAudit && (
        <AuditDrawerPanel
          audit={activeAudit}
          currentUser={currentUser}
          users={users}
          teams={teams}
          taskTemplateCategories={taskTemplateCategories}
          onClose={()=>setDrawerAuditId(null)}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          onDeleteComment={handleDeleteComment}
          onSaveAuditField={handleAuditDrawerAction}
          onGenerateTasks={handleGenerateAuditTasks}
          onCreateAuditTask={handleCreateAuditTask}
          onUpdateAuditTask={handleUpdateAuditTask}
          onDeleteAuditTask={handleDeleteAuditTask}
          onCreateAuditTaskComment={handleCreateAuditTaskComment}
          addNotification={addNotification}
          showToast={showToast}
          auditStages={auditStages}
          stageColorMap={stageColorMap}
        />
      )}

      <DialogPrimitives.Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogPrimitives.DialogContent className="max-w-[560px]">
          <DialogPrimitives.DialogHeader>
            <DialogPrimitives.DialogTitle>Start New Audit</DialogPrimitives.DialogTitle>
          </DialogPrimitives.DialogHeader>

          <div className="flex flex-col gap-3">
            <FieldGroup>
              <FieldLabel required>Company Name</FieldLabel>
              <Input
                placeholder="e.g. Acme Corp"
                value={newForm.name}
                onChange={e=>setNewForm(p=>({...p,name:e.target.value}))}
                onKeyDown={e=>{if(e.key==='Enter')handleCreate();}}
                autoFocus
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Audit Type</FieldLabel>
              <Select value={newForm.auditTypeId} onChange={e=>setNewForm(p=>({...p,auditTypeId:e.target.value}))}>
                <option value="">— Select type —</option>
                {auditTypes.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Contact Person</FieldLabel>
              <Select value={newForm.contactId} onChange={e=>setNewForm(p=>({...p,contactId:e.target.value}))}>
                <option value="">— Select user —</option>
                {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Task Category</FieldLabel>
              <Select value={newForm.taskTemplateCategoryId} onChange={e=>setNewForm(p=>({...p,taskTemplateCategoryId:e.target.value}))}>
                <option value="">— Select category —</option>
                {taskTemplateCategories.map(category=><option key={category.id} value={category.id}>{category.name}</option>)}
              </Select>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Language</FieldLabel>
              <Select value={newForm.languageId} onChange={e=>setNewForm(p=>({...p,languageId:e.target.value}))}>
                <option value="">— Select language —</option>
                {languages.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Status</FieldLabel>
              <Select value={newForm.status} onChange={e=>setNewForm(p=>({...p,status:e.target.value}))}>
                {auditStages.map(s=><option key={s} value={s}>{s}</option>)}
              </Select>
            </FieldGroup>

            <div className="mt-1 rounded-lg bg-[rgba(59,127,250,0.04)] border border-[rgba(59,127,250,0.14)] px-3.5 py-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-rc-text-secondary mb-1">Workflow</div>
              <div className="text-[12px] text-rc-text-secondary leading-[1.55]">The audit will be created in the selected stage and keep the current API-backed data flow intact.</div>
            </div>
          </div>

          <DialogPrimitives.DialogFooter>
            <Button variant="secondary" onClick={()=>setShowNewModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newForm.name.trim()||saving}>
              {saving?'Creating…':'Create Audit'}
            </Button>
          </DialogPrimitives.DialogFooter>
        </DialogPrimitives.DialogContent>
      </DialogPrimitives.Dialog>
    </div>
  );
}

export default Audits;
